import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatPhone } from "@/lib/api";
import { useUiState } from "@/lib/uiState";
import { useAuth } from "@/context/AuthContext";
import { Search, Plus, Pencil, Trash2, Eye, EyeOff, Filter, MailX, UserCheck, ArrowUp, ArrowDown, ChevronsUpDown, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import MapPanel from "@/components/MapPanel";
import { Switch } from "@/components/ui/switch";

const StatusBadge = ({ status }) => {
  const map = {
    active: "bg-emerald-100 text-emerald-800",
    unclaimed: "bg-slate-100 text-slate-700",
    needs_review: "bg-amber-100 text-amber-800",
  };
  const label = { active: "Active", unclaimed: "Unclaimed", needs_review: "Needs Review" };
  return <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${map[status] || map.unclaimed}`}>{label[status] || status}</span>;
};

const sortValue = (a, key) => {
  switch (key) {
    case "name": return (a.last_name || (a.name || "").trim().split(" ").slice(-1)[0] || "").toLowerCase();
    case "class": return a.class_year ?? -Infinity;
    case "city": return (a.city || "").toLowerCase();
    case "state": return (a.state || "").toLowerCase();
    case "status": return (a.profile_status || "").toLowerCase();
    default: return "";
  }
};

export default function Directory() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [classYear, setClassYear] = useState(sp.get("class_year") || "");
  const [state, setState] = useState(sp.get("state") || "");
  const [living, setLiving] = useState(sp.get("living_status") || "");
  const [noEmail, setNoEmail] = useState(sp.get("no_email") === "true");
  const [hideContact, setHideContact] = useState(true);
  const [sort, setSort] = useUiState("ud_directory_sort", { key: null, dir: "asc" });
  const [listCollapsed, setListCollapsed] = useUiState("ud_directory_collapsed", false);
  const [hideDeceased, setHideDeceased] = useUiState("ud_directory_hide_deceased", false);
  const [data, setData] = useState({ count: 0, alumni: [] });
  const [filters, setFilters] = useState({ class_years: [], states: [] });
  const [loading, setLoading] = useState(true);
  const { isAdmin, impersonate } = useAuth();
  const navigate = useNavigate();

  const emulate = async (id, e) => {
    e.stopPropagation();
    try {
      await impersonate(id);
      navigate("/experience");
    } catch (err) {
      toast.error("Could not start preview");
    }
  };

  useEffect(() => {
    api.get("/alumni/filters").then((r) => setFilters(r.data));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/alumni", { params: { q, class_year: classYear, state, living_status: living, no_email: noEmail ? "true" : "" } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [q, classYear, state, living, noEmail]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortIcon = (k) => (sort.key === k
    ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
    : <ChevronsUpDown className="h-3 w-3 opacity-40" />);
  const sortedAlumni = useMemo(() => {
    if (!sort.key) return data.alumni;
    const arr = [...data.alumni];
    arr.sort((a, b) => {
      const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data.alumni, sort]);

  const visibleAlumni = useMemo(
    () => (hideDeceased ? sortedAlumni.filter((a) => a.living_status !== "deceased") : sortedAlumni),
    [sortedAlumni, hideDeceased]
  );

  // How many mappable deceased alumni are being hidden from the map right now (for the map note).
  const hiddenDeceased = useMemo(() => {
    if (!hideDeceased) return 0;
    return sortedAlumni.filter(
      (a) => a.living_status === "deceased" && ((typeof a.lat === "number" && typeof a.lng === "number") || a.state)
    ).length;
  }, [hideDeceased, sortedAlumni]);

  useEffect(() => {
    localStorage.setItem("ud_directory_order", JSON.stringify(visibleAlumni.map((a) => a.id)));
    try { sessionStorage.removeItem("ud_record_return"); } catch {}
  }, [visibleAlumni]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this alumnus permanently?")) return;
    await api.delete(`/alumni/${id}`);
    toast.success("Alumnus deleted");
    load();
  };

  const toggleEmail = async (id, currentOptOut) => {
    const nextOptOut = !currentOptOut;
    setData((d) => ({ ...d, alumni: d.alumni.map((a) => (a.id === id ? { ...a, email_opt_out: nextOptOut } : a)) }));
    try {
      await api.patch(`/alumni/${id}/email-opt-out`, { opt_out: nextOptOut });
      toast.success(nextOptOut ? "Emails turned off for this alumnus" : "Emails turned on for this alumnus");
    } catch (err) {
      setData((d) => ({ ...d, alumni: d.alumni.map((a) => (a.id === id ? { ...a, email_opt_out: currentOptOut } : a)) }));
      toast.error("Could not update email preference");
    }
  };

  const bulkToggleEmails = async () => {
    const withEmail = data.alumni.filter((a) => a.email);
    const allOn = withEmail.length > 0 && withEmail.every((a) => !a.email_opt_out);
    const optOut = allOn; // all currently on -> turn all off; otherwise turn all on
    try {
      const { data: r } = await api.post("/alumni/email-opt-out/bulk", { opt_out: optOut });
      load();
      toast.success(optOut
        ? `Emails turned OFF for ${r.changed} alumni`
        : `Emails turned ON for ${r.changed} alumni${r.skipped_self_unsubscribed ? ` (${r.skipped_self_unsubscribed} kept opted out)` : ""}`);
    } catch { toast.error("Could not bulk-update emails"); }
  };

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Registry</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Alumni Directory</h1>
        </div>
        {isAdmin && (
          <button data-testid="directory-add-button" onClick={() => navigate("/alumni/new")} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
            <Plus className="h-4 w-4" /> Add Alumnus
          </button>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-4 mb-5">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="alumni-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone, class year, employer…"
              className="w-full pl-10 pr-3 py-2.5 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select data-testid="filter-class-year" value={classYear} onChange={(e) => setClassYear(e.target.value)} className="px-3 py-2.5 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
              <option value="">All Classes</option>
              {filters.class_years.map((y) => <option key={y} value={y}>{`Class of ${y}`}</option>)}
            </select>
            <select data-testid="filter-state" value={state} onChange={(e) => setState(e.target.value)} className="px-3 py-2.5 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
              <option value="">All States</option>
              {filters.states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="filter-living" value={living} onChange={(e) => setLiving(e.target.value)} className="px-3 py-2.5 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
              <option value="">Any Status</option>
              <option value="living">Living</option>
              <option value="deceased">Deceased</option>
            </select>
            <button
              type="button"
              data-testid="toggle-no-email"
              onClick={() => setNoEmail((v) => !v)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-sm font-medium transition-colors ${noEmail ? "bg-primary text-white border-primary" : "border-input bg-background text-muted-foreground hover:bg-muted"}`}
            >
              <MailX className="h-4 w-4" /> No email
            </button>
            <button
              type="button"
              data-testid="toggle-hide-contact"
              onClick={() => setHideContact((v) => !v)}
              title={hideContact ? "Contact info is hidden — click to reveal" : "Contact info is visible — click to hide"}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-sm font-medium transition-colors ${hideContact ? "bg-secondary text-white border-secondary" : "border-input bg-background text-muted-foreground hover:bg-muted"}`}
            >
              {hideContact ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {hideContact ? "Contact hidden" : "Contact shown"}
            </button>
          </div>
        </div>
      </div>

      <MapPanel
        alumni={visibleAlumni}
        hiddenNote={hiddenDeceased ? `${hiddenDeceased} deceased hidden` : ""}
        canRefresh={isAdmin}
        classYear={classYear || null}
        onGeocodeDone={load}
      />

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className={`w-full px-4 py-3 flex items-center gap-3 ${listCollapsed ? "" : "border-b border-border"}`}>
          <button
            type="button"
            data-testid="toggle-list-collapse"
            onClick={() => setListCollapsed((v) => !v)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors flex-1 text-left"
          >
            <Filter className="h-4 w-4" /> <span data-testid="results-count">{loading ? "Loading…" : `${hideDeceased ? visibleAlumni.length : data.count} alumni`}</span>
          </button>
          <button
            type="button"
            data-testid="toggle-hide-deceased"
            onClick={() => setHideDeceased((v) => !v)}
            title={hideDeceased ? "Show deceased alumni" : "Hide deceased alumni"}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border transition-colors ${hideDeceased ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-muted"}`}
          >
            {hideDeceased ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} Hide deceased
          </button>
          <button
            type="button"
            onClick={() => setListCollapsed((v) => !v)}
            className="text-muted-foreground hover:text-foreground"
          >
            {listCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
        </div>
        {!listCollapsed && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-4 py-3"><button data-testid="sort-name" onClick={() => toggleSort("name")} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground">Name {sortIcon("name")}</button></th>
                <th className="px-4 py-3"><button data-testid="sort-class" onClick={() => toggleSort("class")} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground">Class {sortIcon("class")}</button></th>
                <th className="px-4 py-3"><button data-testid="sort-city" onClick={() => toggleSort("city")} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground">City {sortIcon("city")}</button></th>
                <th className="px-4 py-3"><button data-testid="sort-state" onClick={() => toggleSort("state")} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground">State {sortIcon("state")}</button></th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3"><button data-testid="sort-status" onClick={() => toggleSort("status")} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground">Status {sortIcon("status")}</button></th>
                {isAdmin && <th className="px-4 py-3"><button data-testid="bulk-toggle-emails" onClick={bulkToggleEmails} title="Turn all emails on/off (self opt-outs are respected)" className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground">Emails <MailX className="h-3 w-3" /></button></th>}
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleAlumni.map((a) => (
                <tr
                  key={a.id}
                  data-testid={`alumni-row-${a.id}`}
                  onClick={() => navigate(`/alumni/${a.id}`)}
                  className="border-b border-border last:border-0 hover:bg-muted/60 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {a.photo ? (
                        <img src={fileUrl(a.photo)} alt="" className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-secondary/10 flex items-center justify-center text-xs font-semibold text-secondary">{(a.name || "?").trim().slice(0, 1)}</div>
                      )}
                      <div>
                        <div className="font-medium">{a.name.trim() || "—"}</div>
                        {a.living_status === "deceased" && <span className="text-[10px] text-slate-500">Deceased</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{a.class_year ?? "—"}</td>
                  <td className="px-4 py-3">{a.city || "—"}</td>
                  <td className="px-4 py-3">{a.state || "—"}</td>
                  <td className="px-4 py-3 max-w-[200px] truncate">
                    {a.email ? (hideContact ? <span className="text-muted-foreground tracking-widest select-none">••••••••</span> : a.email) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {a.phone ? (hideContact ? <span className="text-muted-foreground tracking-widest select-none">••••••</span> : formatPhone(a.phone)) : "—"}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={a.profile_status} /></td>
                  {isAdmin && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <Switch
                          data-testid={`email-toggle-${a.id}`}
                          checked={!a.email_opt_out}
                          disabled={!a.email}
                          onCheckedChange={() => toggleEmail(a.id, a.email_opt_out)}
                        />
                        <span className={`text-[11px] ${a.email_opt_out ? "text-muted-foreground" : "text-emerald-700"}`}>
                          {a.email_opt_out ? "Off" : "On"}
                        </span>
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button title="View" onClick={(e) => { e.stopPropagation(); navigate(`/alumni/${a.id}`); }} className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-foreground">
                        <Eye className="h-4 w-4" />
                      </button>
                      {isAdmin && (
                        <>
                          <button data-testid={`emulate-${a.id}`} title="Emulate this alumnus" onClick={(e) => emulate(a.id, e)} className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-accent">
                            <UserCheck className="h-4 w-4" />
                          </button>
                          <button data-testid={`edit-${a.id}`} title="Edit" onClick={(e) => { e.stopPropagation(); navigate(`/alumni/${a.id}/edit`); }} className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-primary">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button data-testid={`delete-${a.id}`} title="Delete" onClick={(e) => del(a.id, e)} className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && visibleAlumni.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">No alumni match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
